import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminTherapists from "@/pages/admin/AdminTherapists";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminLMS from "@/pages/admin/AdminLMS";
import AdminPerformance from "@/pages/admin/AdminPerformance";
import AdminTiers from "@/pages/admin/AdminTiers";
import AdminEarnings from "@/pages/admin/AdminEarnings";
import AdminRetention from "@/pages/admin/AdminRetention";
import AdminReports from "@/pages/admin/AdminReports";
import TherapistDashboard from "@/pages/therapist/TherapistDashboard";
import TherapistPerformance from "@/pages/therapist/TherapistPerformance";
import TherapistEarnings from "@/pages/therapist/TherapistEarnings";
import TherapistCareer from "@/pages/therapist/TherapistCareer";
import TherapistOnboarding from "@/pages/therapist/TherapistOnboarding";

// Layouts
import AdminLayout from "@/layouts/AdminLayout";
import TherapistLayout from "@/layouts/TherapistLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      // Get intended role from sessionStorage (set by login page)
      const intendedRole = sessionStorage.getItem('intended_role') || 'therapist';
      sessionStorage.removeItem('intended_role'); // Clean up
      
      axios.post(`${API}/auth/session`, { 
        session_id: sessionId,
        intended_role: intendedRole 
      }, { withCredentials: true })
        .then(response => {
          const user = response.data;
          // Clear hash
          window.history.replaceState(null, "", window.location.pathname);
          // Navigate based on role
          if (user.role === "admin") {
            navigate("/admin", { state: { user } });
          } else {
            navigate("/therapist", { state: { user } });
          }
        })
        .catch(error => {
          console.error("Auth error:", error);
          navigate("/login");
        });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Check auth with server
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(response => {
        setUser(response.data);
        setIsAuthenticated(true);
        
        // Redirect if wrong role
        if (requiredRole && response.data.role !== requiredRole) {
          if (response.data.role === "admin") {
            navigate("/admin");
          } else {
            navigate("/therapist");
          }
        }
      })
      .catch(() => {
        setIsAuthenticated(false);
        navigate("/login");
      });
  }, [location.state, requiredRole, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children({ user });
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in hash (must be synchronous, not in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminDashboard /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/applications" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminApplications /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/therapists" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminTherapists /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/verification" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminVerification /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/lms" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminLMS /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/performance" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminPerformance /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/tiers" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminTiers /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/earnings" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminEarnings /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/retention" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminRetention /></AdminLayout>}
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requiredRole="admin">
          {({ user }) => <AdminLayout user={user}><AdminReports /></AdminLayout>}
        </ProtectedRoute>
      } />

      {/* Therapist Routes */}
      <Route path="/therapist" element={
        <ProtectedRoute requiredRole="therapist">
          {({ user }) => <TherapistLayout user={user}><TherapistDashboard /></TherapistLayout>}
        </ProtectedRoute>
      } />
      <Route path="/therapist/performance" element={
        <ProtectedRoute requiredRole="therapist">
          {({ user }) => <TherapistLayout user={user}><TherapistPerformance /></TherapistLayout>}
        </ProtectedRoute>
      } />
      <Route path="/therapist/earnings" element={
        <ProtectedRoute requiredRole="therapist">
          {({ user }) => <TherapistLayout user={user}><TherapistEarnings /></TherapistLayout>}
        </ProtectedRoute>
      } />
      <Route path="/therapist/career" element={
        <ProtectedRoute requiredRole="therapist">
          {({ user }) => <TherapistLayout user={user}><TherapistCareer /></TherapistLayout>}
        </ProtectedRoute>
      } />
      <Route path="/therapist/onboarding" element={
        <ProtectedRoute requiredRole="therapist">
          {({ user }) => <TherapistLayout user={user}><TherapistOnboarding /></TherapistLayout>}
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
